/**
 * Document validity capture, rendered under a successfully uploaded file in the
 * workflow form. Bimodal, driven by the field's validity kind (migration 0037):
 *
 *   · 'expiry' (identity: INE / passport / driver license) — the operator types
 *     the stamped EXPIRY date. Cascade: day requires month, month requires year;
 *     a partial date like "December 2026" is allowed.
 *   · 'issued' (proof of address, SAT certificate) — the operator types the
 *     ISSUE date; the backend computes the expiry as issued_at + catalog window.
 *     A full year/month/day is required (the window must be exact).
 *   · 'none' (signature, SSN/SIN) — renders nothing.
 *
 * Why this lives separate from the upload itself: the upload arrives eagerly
 * when the user picks a file; the date is something the operator (client OR
 * staff during review) types AFTER reading the document. Splitting the two lets
 * the file save first and the date be added later without re-uploading.
 *
 * Backend: PATCH /workflow/cases/{caseId}/documents/{documentId}/expiry
 *   { expiry_year, expiry_month, expiry_day }        (identity)
 *   { issued_year, issued_month, issued_day }        (address / fiscal)
 * The same resolution + validation runs server-side (defense-in-depth).
 */
import { useState } from 'react';
import { getApiErrorMessage } from '../../lib/apiError';
import { documentValidityMode } from '../../lib/documentValidity';
import { colors } from '../../styles/theme';

export interface ExpiryDraft {
  year: string;   // raw input — empty string when blank
  month: string;
  day: string;
}

export interface ExpirySavedState {
  expires_at: string | null;
  expiry_granularity: 'exact' | 'month' | 'year' | null;
}

/** Body accepted by the validity PATCH — expiry OR issue date, per mode. */
export interface SaveValidityBody {
  expiry_year?: number | null;
  expiry_month?: number | null;
  expiry_day?: number | null;
  issued_year?: number | null;
  issued_month?: number | null;
  issued_day?: number | null;
}

interface Props {
  documentId: string;
  /** The workflow field key — decides whether we capture an expiry (identity)
   *  or an issue date (proof of address / tax certificate). */
  fieldKey: string;
  /** The document's current stored expiry, if any. */
  initial: ExpirySavedState;
  /** True when the parent flow allows editing. When false, read-only. */
  enabled: boolean;
  /** Closure that PATCHes the validity to the backend (parent provides caseId). */
  onSave: (documentId: string, body: SaveValidityBody) => Promise<ExpirySavedState>;
}

const MONTH_NAMES = [
  '',  // 1-indexed placeholder
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Range-checks a single component; returns an error message or null. */
function outOfRange(value: string, min: number, max: number, label: string): string | null {
  if (!value) return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n < min || n > max) return `${label} must be between ${min} and ${max}.`;
  return null;
}

/** Validates the draft for the given mode. Returns an error message or null. */
function validate(draft: ExpiryDraft, mode: 'expiry' | 'issued'): string | null {
  const year = draft.year.trim();
  const month = draft.month.trim();
  const day = draft.day.trim();
  if (mode === 'issued') {
    // Issue date needs a full Y/M/D so the relative window is exact.
    if (!year || !month || !day) return 'Please enter the full issue date (day, month and year).';
  } else {
    // Expiry cascades: a partial date is allowed.
    if (day && !month) return 'Day requires a month.';
    if (month && !year) return 'Month requires a year.';
  }
  return (
    outOfRange(year, 1900, 2100, 'Year')
    ?? outOfRange(month, 1, 12, 'Month')
    ?? outOfRange(day, 1, 31, 'Day')
  );
}

function formatSavedDate(saved: ExpirySavedState): string {
  const d = new Date(saved.expires_at as string);
  if (saved.expiry_granularity === 'year') return String(d.getUTCFullYear());
  if (saved.expiry_granularity === 'month') return `${MONTH_NAMES[d.getUTCMonth() + 1]} ${d.getUTCFullYear()}`;
  return `${MONTH_NAMES[d.getUTCMonth() + 1]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

function describeSaved(saved: ExpirySavedState, mode: 'expiry' | 'issued'): string | null {
  if (!saved.expires_at) return null;
  const dateLabel = formatSavedDate(saved);
  return mode === 'issued' ? `Valid until: ${dateLabel}` : `Captured as: ${dateLabel}`;
}

/** Re-hydrate the inputs from a previously-saved EXPIRY. Only meaningful in
 *  expiry mode — in issued mode the stored value is the computed expiry, not the
 *  issue date, so the inputs start blank for a fresh recapture. */
function draftFromSaved(saved: ExpirySavedState, mode: 'expiry' | 'issued'): ExpiryDraft {
  if (mode === 'issued' || !saved.expires_at || !saved.expiry_granularity) {
    return { year: '', month: '', day: '' };
  }
  const d = new Date(saved.expires_at);
  const year = String(d.getUTCFullYear());
  if (saved.expiry_granularity === 'year') return { year, month: '', day: '' };
  const month = String(d.getUTCMonth() + 1);
  if (saved.expiry_granularity === 'month') return { year, month, day: '' };
  return { year, month, day: String(d.getUTCDate()) };
}

function toBody(draft: ExpiryDraft, mode: 'expiry' | 'issued'): SaveValidityBody {
  const y = draft.year ? Number(draft.year) : null;
  const m = draft.month ? Number(draft.month) : null;
  const d = draft.day ? Number(draft.day) : null;
  return mode === 'issued'
    ? { issued_year: y, issued_month: m, issued_day: d }
    : { expiry_year: y, expiry_month: m, expiry_day: d };
}

export default function DocumentExpiryCapture({
  documentId,
  fieldKey,
  initial,
  enabled,
  onSave,
}: Readonly<Props>): JSX.Element | null {
  const rawMode = documentValidityMode(fieldKey);
  const mode: 'expiry' | 'issued' = rawMode === 'issued' ? 'issued' : 'expiry';
  const [draft, setDraft] = useState<ExpiryDraft>(() => draftFromSaved(initial, mode));
  const [saved, setSaved] = useState<ExpirySavedState>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // A document that never carries a date (signature, SSN/SIN) renders nothing.
  if (rawMode === 'none') return null;

  const baseline = draftFromSaved(saved, mode);
  const dirty =
    draft.year !== baseline.year || draft.month !== baseline.month || draft.day !== baseline.day;

  const handleSave = async (): Promise<void> => {
    const validationError = validate(draft, mode);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await onSave(documentId, toBody(draft, mode));
      setSaved(result);
      setFlash('Saved.');
      globalThis.setTimeout(() => setFlash(null), 2500);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not save.'));
    } finally {
      setSaving(false);
    }
  };

  const summary = describeSaved(saved, mode);
  const title = mode === 'issued' ? 'Issue date' : 'Document expiry';
  const help = mode === 'issued'
    ? 'Enter the date this document was issued. It must be recent — we use it to confirm the document is still current.'
    : 'If the document shows an expiration date, fill in what you can read. Day and month are optional — leave them blank if the document only shows e.g. “December 2026” or just a year.';
  const saveLabel = mode === 'issued' ? 'Save issue date' : 'Save expiry';

  return (
    <div
      className="mt-3 p-3 rounded-md"
      style={{ background: colors.canvas, border: `1px solid ${colors.border}` }}
    >
      <div className="text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
        {title}
        {mode === 'issued' && <span aria-label="required" style={{ color: colors.danger }}> *</span>}
      </div>
      <p className="text-xs mb-3" style={{ color: colors.muted }}>{help}</p>

      <div className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="block text-xs mb-1" style={{ color: colors.muted }}>Day</span>
          <input
            type="number" min="1" max="31" inputMode="numeric"
            value={draft.day}
            disabled={!enabled || saving}
            onChange={(e) => setDraft((d) => ({ ...d, day: e.target.value }))}
            placeholder="DD"
            className="w-16 px-2 py-1.5 rounded-md text-sm"
            style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.ink }}
          />
        </label>
        <label className="block">
          <span className="block text-xs mb-1" style={{ color: colors.muted }}>Month</span>
          <input
            type="number" min="1" max="12" inputMode="numeric"
            value={draft.month}
            disabled={!enabled || saving}
            onChange={(e) => setDraft((d) => ({ ...d, month: e.target.value }))}
            placeholder="MM"
            className="w-16 px-2 py-1.5 rounded-md text-sm"
            style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.ink }}
          />
        </label>
        <label className="block">
          <span className="block text-xs mb-1" style={{ color: colors.muted }}>Year</span>
          <input
            type="number" min="1900" max="2100" inputMode="numeric"
            value={draft.year}
            disabled={!enabled || saving}
            onChange={(e) => setDraft((d) => ({ ...d, year: e.target.value }))}
            placeholder="YYYY"
            className="w-20 px-2 py-1.5 rounded-md text-sm"
            style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.ink }}
          />
        </label>
        {enabled && (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="px-3 py-1.5 rounded-md text-xs font-semibold"
            style={{
              background: (saving || !dirty) ? colors.border : colors.gold,
              color: (saving || !dirty) ? colors.muted : colors.navy,
              cursor: (saving || !dirty) ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : saveLabel}
          </button>
        )}
      </div>

      {error && <div className="mt-2 text-xs" style={{ color: colors.danger }}>{error}</div>}
      {!error && flash && <div className="mt-2 text-xs" style={{ color: colors.success }}>{flash}</div>}
      {!error && !flash && summary && (
        <div className="mt-2 text-xs" style={{ color: colors.inkSoft }}>{summary}</div>
      )}
    </div>
  );
}
